/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * DISTRIBUTOR PROGRESS (item 232).
 *
 * The product answered "can distributors see their own progress?" with "yes"
 * while no such surface existed anywhere — no route, no view, nothing. This is
 * it: one read-only page per distributor showing every program they are running
 * for you and how each goal is tracking, pulled from the same resolver the
 * scoring screen uses so the two can never tell different stories.
 *
 * READ-ONLY BY CONSTRUCTION. Nothing on this page writes, and it only ever shows
 * programs that have been handed over (sent / active / scoring / completed) —
 * a draft or a program still in review is an internal document and showing it
 * under a distributor's name would leak an offer nobody has agreed to.
 *
 * Today it is an internal view a rep opens and walks a brand manager through
 * (or exports from). Giving the distributor their own no-login link needs a
 * public route and a public edge function, neither of which lives in this
 * module — see ARCHITECTURE.md, "Not built here".
 */
import { useCallback, useEffect, useState } from "react";
import HomeButton from "../../../shared/components/HomeButton.jsx";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import {
  listDistributorsWithPrograms, getDistributorPrograms, getIncentiveAccountId,
} from "../utils/incentiveService.js";
import { exportProgramPdf } from "../utils/incentivePdf.js";
import { STATUS_LABEL } from "../utils/constants.js";
import StatusBadge from "./StatusBadge.jsx";
import ProgressPanel from "./ProgressPanel.jsx";
import "./incentive.css";

function fmtRange(a, b) {
  if (!a && !b) return "No dates set";
  const f = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
  return `${f(a)} – ${f(b)}`;
}

/** The index: pick a distributor. */
function DistributorList({ rows, onOpen }) {
  if (rows.length === 0) {
    return (
      <div className="it-empty">
        <p>No programs have been handed to a distributor yet.</p>
        <p className="muted">Once a program is sent, its progress shows up here for the distributor it targets.</p>
      </div>
    );
  }
  return (
    <div className="it-list">
      {rows.map((d) => (
        <div key={d.distributor} className="it-row" role="button" tabIndex={0}
             onClick={() => onOpen(d.distributor)}
             onKeyDown={(e) => { if (e.key === "Enter") onOpen(d.distributor); }}>
          <div className="it-row-main">
            <div className="it-row-name">{d.distributor}</div>
            <div className="it-row-meta">
              <span>{d.total} program{d.total === 1 ? "" : "s"}</span>
              {d.live > 0 && <span>{d.live} in flight</span>}
              {d.completed > 0 && <span>{d.completed} completed</span>}
            </div>
          </div>
          <div className="it-row-side"><span className="caption">View progress →</span></div>
        </div>
      ))}
    </div>
  );
}

export default function DistributorProgressPage({ distributor, onBack, onHome, onOpenDistributor }) {
  const [rows, setRows] = useState(null);
  const [bundles, setBundles] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [account, setAccount] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    getIncentiveAccountId().then(setAccountId).catch(() => {});
    getMyPrimaryAccount().then((a) => setAccount(a || null)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setErr(null);
    try {
      if (distributor) { setBundles(await getDistributorPrograms(distributor)); }
      else { setRows(await listDistributorsWithPrograms()); }
    } catch (e) { setErr(e.message); setRows([]); setBundles([]); }
  }, [distributor]);

  useEffect(() => { load(); }, [load]);

  async function handleExport(bundle) {
    setExportingId(bundle.program.id); setErr(null);
    try {
      const acct = account || (await getMyPrimaryAccount());
      await exportProgramPdf({
        program: bundle.program, goals: bundle.goals, scores: bundle.scores,
        accountName: acct?.name || "", logoUrl: acct?.logoUrl || "",
        primaryColor: acct?.primaryColor || "", secondaryColor: acct?.secondaryColor || "",
      });
    } catch (e) {
      console.error("[incentive] distributor export", e);
      setErr(`Could not generate the PDF${e?.message ? `: ${e.message}` : "."}`);
    } finally { setExportingId(null); }
  }

  // ── Index ────────────────────────────────────────────────────────────────
  if (!distributor) {
    return (
      <section className="settings-card wide">
        <HomeButton onClick={onHome} />
        <div className="it-head" style={{ marginTop: 10 }}>
          <div>
            <h1 style={{ marginBottom: 6 }}>Distributor progress</h1>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              What each distributor is running for you, and how it&apos;s tracking.
            </p>
          </div>
          <div className="it-actions">
            <button className="secondary" onClick={onBack}>All programs</button>
          </div>
        </div>
        {err && <p className="error">{err}</p>}
        {rows === null ? <p className="muted">Loading…</p> : <DistributorList rows={rows} onOpen={(d) => onOpenDistributor?.(d)} />}
      </section>
    );
  }

  // ── One distributor ──────────────────────────────────────────────────────
  return (
    <section className="settings-card wide">
      <button className="it-backlink" onClick={onBack}>← All distributors</button>

      <div className="it-head">
        <div>
          <h1 style={{ marginBottom: 6 }}>{distributor}</h1>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Read-only. Programs handed to this distributor, and how each goal is tracking.
          </p>
        </div>
      </div>

      {err && <p className="error">{err}</p>}

      {bundles === null ? (
        <p className="muted">Loading…</p>
      ) : bundles.length === 0 ? (
        <div className="it-empty">
          <p>Nothing has been handed to {distributor} yet.</p>
        </div>
      ) : (
        bundles.map((bundle) => (
          <div key={bundle.program.id} className="it-dist-program">
            <div className="it-head" style={{ marginBottom: 10 }}>
              <div>
                <h2 style={{ margin: "0 0 6px" }}>{bundle.program.name}</h2>
                <div className="it-row-meta">
                  <span>{fmtRange(bundle.program.start_date, bundle.program.end_date)}</span>
                  {Array.isArray(bundle.program.target_states) && bundle.program.target_states.length > 0 && (
                    <span>{bundle.program.target_states.join(", ")}</span>
                  )}
                  <span>{STATUS_LABEL[bundle.program.status] || bundle.program.status}</span>
                </div>
              </div>
              <div className="it-actions">
                <StatusBadge status={bundle.program.status} />
                <button className="secondary" onClick={() => handleExport(bundle)} disabled={exportingId === bundle.program.id || bundle.goals.length === 0}>
                  {exportingId === bundle.program.id ? "…" : "Export PDF"}
                </button>
              </div>
            </div>

            <ProgressPanel
              program={bundle.program}
              goals={bundle.goals}
              scores={bundle.scores}
              accountId={accountId}
              heading="Goals"
            />
          </div>
        ))
      )}
    </section>
  );
}
